import { Config } from '../config.model';
import { InsuredData } from '../insurance-target.model';
import { deepCloneProduct, getProductTemporalityConditions, Product } from '../product.model';
import { FutureGoal } from './future-goal.model';
import {
  OfferingProductConfiguration,
  OfferingProductConfigurationParams,
  OfferingProductsDefinition
} from './offering-product-definition.model';
import { Person } from './person.model';

export interface OfferingProduct {
  definition?: OfferingProductsDefinition;
  person?: Person;
  futureGoal?: FutureGoal;
  /**
   * Offering product and goal purpose (generic): use `incidentals` for Incidentals protection. `patrimony/car`, `patrimony/house` are other kind of goals.
   */
  purpose?: string;
  /**
   * Available configurations for this `person` or `futureGoal`
   */
  availableConfigurations?: OfferingProductConfiguration[];
  recommendedConfiguration?: OfferingProductConfiguration;
  selectedConfiguration?: OfferingProductConfiguration;
  /**
   * Additional labels / properties for the product: use labels to visual customization, params to calculations customization
   */
  labels?: any;
}

/**
 * This generates an OfferingProduct using the data available on a Product, it does not fill
 * data about the person, futureGoal or purpose associated.
 * @param product The Product from which extract the data
 * @param config The Config from which extract the product associated definition and configurations
 * @returns An OfferingProduct filled with all the data that can be retrieved from a Product, leaving
 * empty the rest of the data.
 */
export function generateOfferingProductFromProduct(
  product: Product,
  config: Config,
  insured: InsuredData
): OfferingProduct {
  const offeringProductDefinition = config.sourceConfigurations?.offeringProductsDefinitions.find(
    o => o.identifier === product.id
  );
  const params: OfferingProductConfigurationParams = {};
  switch (product.type) {
    case 'normal':
      if (product.currentAssuredAmount) {
        params.insuredAmount = {
          value: product.currentAssuredAmount.amount,
          currency: product.currentAssuredAmount.currency
        };
      }
      if (product.currentFee) {
        params.feeAmount = {
          value: product.currentFee.amount,
          currency: product.currentFee.currency
        };
      }
      break;
    case 'investment':
      if (product.amountAtEndOfTemporality) {
        params.insuredAmount = {
          value: product.amountAtEndOfTemporality.amount,
          currency: product.amountAtEndOfTemporality.currency
        };
      }
      if (product.currentInvestment) {
        params.feeAmount = {
          value: product.currentInvestment.amount,
          currency: product.currentInvestment.currency
        };
      }
      break;
  }

  return {
    definition: { identifier: offeringProductDefinition?.identifier },
    selectedConfiguration: { identifier: product.selectedTemporality, params }
  };
}

export function generateProductFromOfferingProduct(offeringProduct: OfferingProduct, config: Config): Product {
  const productDef = config.products[offeringProduct.definition?.identifier || ''];
  const product = deepCloneProduct(productDef);
  product.temporalityConditions = getProductTemporalityConditions(offeringProduct.availableConfigurations);
  if (offeringProduct.recommendedConfiguration?.value) {
    product.selectedCurrency = offeringProduct.recommendedConfiguration.value.currency;
    product.selectedTemporality = offeringProduct.recommendedConfiguration.identifier;
    if (
      product.allowedIncreases &&
      product.allowedIncreases.length > 0 &&
      (!product.selectedIncrease ||
        (product.selectedIncrease && product.selectedIncrease.currency !== product.selectedCurrency))
    ) {
      product.selectedIncrease = product.allowedIncreases.filter(increase => {
        return increase.currency === product.selectedCurrency;
      })[0];
    }
  }
  extractParamsFromOfferingProductConfiguration(product, config);

  return product;
}

export function extractParamsFromOfferingProductConfiguration(product: Product, config: Config) {
  const selectedTemporality =
    product.temporalityConditions.find(t => t.id === product.selectedTemporality) || product.temporalityConditions[0];
  const params = selectedTemporality.then.amounts;
  if (params) {
    switch (product.type) {
      case 'normal':
        if (params.insuredAmount) {
          const insuredAmount = params.insuredAmount;
          product.currentAssuredAmount = insuredAmount;
          product.recommendedAssuredAmount = insuredAmount;
          product.currentFee = {
            amount: 0,
            currency: insuredAmount.currency
          };
        }
        if (params.feeAmount) {
          product.currentFee = params.feeAmount;
        }
        break;
      case 'investment':
        if (params.insuredAmount) {
          const insuredAmount = params.insuredAmount;
          product.amountAtEndOfTemporality = insuredAmount;
          product.recommendedAmountAtEndOfTemporality = insuredAmount;
          product.currentInvestment = {
            amount: 0,
            currency: insuredAmount.currency
          };
        }
        if (params.feeAmount) {
          product.currentInvestment = params.feeAmount;
        }
        break;
    }
  }
}
